import { store, store$ } from '@redux/store';
import { EMPTY, filter, fromEvent, map, merge } from 'rxjs';
import { selectSearchTerm } from 'features/articleSearchResultsFacets';
import { segmentPageView, segmentTrack, segmentIdentify } from 'features/segment';
import { selectModal, selectFetchingModal } from 'layout/redux/selectors';
import HousingProductType from "constants/housingProductType";
import typeahead from 'features/articleSearchBox';

const isBlogArticleSearchResultSection =
    (event) => !!event.target.closest('[data-component="blog-article-search-results"]');
const isBlogArticleMostPopularSection =
    (event) => !!event.target.closest('[data-component="blog-article-search-results-most-popular"]');
const isListingForYouSection = (event) => !!event.target.closest("[data-listings-for-you-container]");
const isFavoriteIcon = (event) => !!event.target.closest("[data-favorite-button]");
const isCallToAction = (event) => !!event.target.closest("[data-lead-generator-opener]");
const isStandardCard = (event) => !!event.target.closest("[data-standard-card]");
const isCommunityStandardCard = (dataset) => dataset.type === HousingProductType.Community().getName();
const isPlanStandardCard = (dataset) => dataset.type === HousingProductType.Plan().getName();
const isSpecStandardCard = (dataset) => dataset.type === HousingProductType.Spec().getName();
const isLinkableListingElement = (event) => event.target instanceof HTMLAnchorElement;
const isArticleCard = (event) => !!event.target.closest("[data-article-card]");
const isBreadcrumbItemElement = (event) => event.target?.closest('[data-breadcrumb-item]');
const getStandardCardElement = (event) => event.target.closest("[data-card]");
const getArticleElement = (event) => event.target.closest('[data-article-card]');
const getNavigatorItemElement = (event) => event.target.closest('[data-navigator-item]');
const breadcrumbItems = Array.from(document.querySelectorAll('[data-breadcrumb-item]'));
const backButtonHeaderElement = document.querySelector('[data-back-button-section="header"]');
const backButtonFooterElement = document.querySelector('[data-back-button-section="footer"]');

const bodyClick$ = fromEvent(document.body, 'click');

const onWindowLoad$ = fromEvent(window, "load");

const listingForYouSectionClick$ = bodyClick$.pipe(
    filter((event) => isListingForYouSection(event))
  );


/**
 * Dispatches a Segment Event event when a tag is clicked.
 * @param tag - is the tag clicked in the tag section.
 */
const triggerTagClickedEvent = function(tag) {
  const customProperties = {
    page_type: 'Learning_Center',
    tag_text: tag.dataset.pillText,
    destination_url: tag.dataset.pillUrl,
  };

  store.dispatch(segmentTrack({ eventName: 'Tag_Clicked', customProperties }));
}

const onNavigatorItemClick$ = bodyClick$.pipe(
  map(getNavigatorItemElement),
  filter(Boolean)
);

onNavigatorItemClick$.subscribe(triggerTagClickedEvent);

/**
 * Dispatches a Segment event when a breadcrumb link is clicked.
 * @param breadcrumbLink - is the link element that represents a
 * breadcrumb item in a website's navigation.
 */
const triggerBreadcrumbClickEvent = (breadcrumbLink) => {
  const breadcrumbIndex = breadcrumbItems.findIndex((item) => item === breadcrumbLink);
  const customProperties = {
    breadcrumb_position: breadcrumbIndex + 1,
    destination_url: breadcrumbLink.href,
    link_text: breadcrumbLink.innerText,
  };

  store.dispatch(segmentTrack({ eventName: 'Breadcrumb_clicked', customProperties }));
};

const onBreadcrumbClick$ = bodyClick$.pipe(
  map(isBreadcrumbItemElement),
  filter(Boolean)
);

onBreadcrumbClick$.subscribe(triggerBreadcrumbClickEvent);

/**
 * Dispatches a tracking event with custom properties
 * including the search term value and additional data.
 * @param data - The `data` parameter in the `triggerSearchResultArticlesClick` function is an object
 * that contains additional information related to the article click event. This data could include
 * details such as the article title, author, category, or any other relevant information about the
 * article that was clicked.
 */
const triggerSearchResultArticlesClick = (data) => {
    const searchTermValue = selectSearchTerm(store.getState());
    const customProperties = {
        searchTerm: searchTermValue,
        ...data
    };

    store.dispatch(segmentTrack({ eventName: 'article_click', customProperties }));
};

/**
 * The function `getAreaInteractionType` returns the type of area clicked on a card element in a web
 * page.
 * @param element - Parameter is a reference to an HTML element that you want to extract
 * the interaction type from.
 * @returns The function `getAreaInteractionType` returns the type of area interaction based on the
 * `interactionType` attribute of the provided `element`. If the `interactionType` matches one of the
 * keys in the `areaClickedPerCardElement` object, it returns the corresponding value. Otherwise, it
 * returns 'Article Clicked' as the default value. 
 */
const getAreaInteractionType = (element) => {
    const areaClickedPerCardElement = {
        image: 'image',
        title: 'title',
        callToAction: 'CTA',
        default: 'Article Clicked',
    };
    
    const interactionType = element.dataset.interactionType;
    return areaClickedPerCardElement[interactionType] || areaClickedPerCardElement.default;
};

/* Observable stream `articleSearchResultSectionClick$` using RxJS
operators. Here's a breakdown of what each part of the code is doing: */
const articleSearchResultSectionClick$ = bodyClick$.pipe(
  filter((event) => isBlogArticleSearchResultSection(event) && isArticleCard(event)),
    map(event => ({
        articleCard: getArticleElement(event),
        interactionType: getAreaInteractionType(event.target),
    }))
);

/* Observable stream `articleClickedEvent$` that listens for click
events within a blog article search result section. When a click event is detected, it maps the
event data to extract relevant information such as the article ID, article title, and interaction
type from the clicked article card. This extracted data is then structured into an object with
properties `articleId`, `articleTitle`, and `interactionType`. This object represents the details of
the article that was clicked within the search results section. */
const articleClickedEvent$ = articleSearchResultSectionClick$.pipe(
    map(data => ({
        articleId: data.articleCard.dataset.articleSlug,
        articleTitle: data.articleCard.dataset.articleTitle,
        interactionType: data.interactionType,
    })),
);

articleClickedEvent$.subscribe(triggerSearchResultArticlesClick);

/* ***** Back Button GA4 Events ***** */

/**
* Dispatches a Segment event when a back button
* element is clicked, capturing relevant custom properties.
* @param element - The `element` parameter in the `triggerBackButtonClickedEvent` function is a
* reference to the HTML element that represents the back button that was clicked.
*/
const triggerBackButtonClickedEvent = (element) => {

  const buttonType = element.dataset.backButtonType;

  const customProperties = {
    button_type: buttonType,
    page_url: window.location.href,
    destination_url: element.href,
    button_text: element.innerText,
    page_section: element.dataset.backButtonSection,
    user_interaction: 'click',
  };

  store.dispatch(segmentTrack({ eventName: 'Button_clicked', customProperties }));
};

/* Observable stream `backButtonHeaderClicked$` that listens for click
events on the `backButtonHeaderElement`. If the `backButtonHeaderElement` exists, it creates an
observable stream using `fromEvent` to listen for click events on the element. The `map` operator is
then used to extract the target element from the click event. If the `backButtonHeaderElement` does
not exist, the observable stream is set to `EMPTY`, indicating that there are no events to listen
to. */
const backButtonHeaderClicked$ = backButtonHeaderElement
  ? fromEvent(backButtonHeaderElement, 'click').pipe(
      map((event) => event.target),
    )
  : EMPTY;


/* Observable stream `backButtonFooterClicked$` that listens for click
events on the `backButtonFooterElement`. If the `backButtonFooterElement` exists, it creates an
observable stream using `fromEvent` to listen for click events on the element. The `map` operator is
then used to extract the target element from the event. If the `backButtonFooterElement` does not
exist, the observable stream is set to `EMPTY`. */
const backButtonFooterClicked$ = backButtonFooterElement
  ? fromEvent(backButtonFooterElement, 'click').pipe(
      map((event) => event.target),
    )
  : EMPTY;

merge(backButtonHeaderClicked$, backButtonFooterClicked$).subscribe(triggerBackButtonClickedEvent);

/* ***** Most Popular Segment Card Events ***** */

/**
 * Dispatches a Segment event for
 * tracking most popular article card clicks with custom properties.
 * @param customProperties - Custom properties are additional data or information that can be passed
 * along with an event or action. In the context of the `triggerSearchResultMostPopularArticlesClick`
 * function, `customProperties` would likely include specific details related to the popular article
 * that was clicked.
 */
const triggerSearchResultMostPopularArticlesClick = (customProperties) => {
    store.dispatch(segmentTrack({ eventName: 'popular_article_clicked', customProperties }));
};

/* Creating an observable stream `mostPopularArticlesSectionClick$` using RxJS
operators. Where we validate if the action comes from the Most Popular Section
and creates the objects needed for the Segment events. */
const mostPopularArticlesSectionClick$ = bodyClick$.pipe(
    filter((event) => isBlogArticleMostPopularSection(event) && isArticleCard(event)),
    map(event => ({
        articleCard: getArticleElement(event),
        interactionType: getAreaInteractionType(event.target),
    }))
);

/* Observable stream `mostPopularArticleClickedEvent$`
using RxJS operators. That builds an object to be used as `customProperties`
to be logged in Segment. */
const mostPopularArticleClickedEvent$ = mostPopularArticlesSectionClick$.pipe(
    map(data => ({
        section: 'most_popular_articles',
        articleId: data.articleCard.dataset.articleSlug,
        articleTitle: data.articleCard.dataset.articleTitle,
        interactionType: data.interactionType,
    })),
);

mostPopularArticleClickedEvent$.subscribe(triggerSearchResultMostPopularArticlesClick);

/**
 * triggerPaidSpecModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidSpecModalSubmitted(modalInformation) {
    const { listingInformation } = modalInformation;
    const customProperties = {
      builder_id: listingInformation.builderId,
      builder_name: listingInformation.brandName,
      button_action: modalInformation.buttonAction,
      city: listingInformation.city,
      community_id: listingInformation.id,
      community_name: listingInformation.communityName,
      home_price: listingInformation.price,
      home_type: listingInformation.homeStatus,
      market_id: listingInformation.marketId,
      market_name: listingInformation.marketName,
      primary_image_url: listingInformation.imageUrl,
      state: listingInformation.stateAbbreviation,
      spec_id: listingInformation.specId,
      zip: listingInformation.zip,
    };
  
    return customProperties;
}

  /**
 * triggerPaidCommunityModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidCommunityModalSubmitted(modalInformation) {
    const { listingInformation } = modalInformation;
    const customProperties = {
      market_id: listingInformation.marketId,
      market_name: listingInformation.marketName,
      builder_id: listingInformation.builderId,
      builder_name: listingInformation.brandName,
      community_id: listingInformation.id,
      community_name: listingInformation.communityName,
      community_status: listingInformation.marketingStatusLabel,
      primary_image_url: listingInformation.imageUrl,
      community_low_price: listingInformation.priceLow,
      community_high_price: listingInformation.priceHigh,
      city: listingInformation.city,
      state: listingInformation.stateAbbreviation,
      zip: listingInformation.zip,
      button_action: modalInformation.buttonAction,
    };
  
    return customProperties;
}

/**
 * triggerPaidPlanModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidPlanModalSubmitted(modalInformation) {
    const { listingInformation } = modalInformation;
    const customProperties = {
      builder_id: listingInformation.builderId,
      builder_name: listingInformation.brandName,
      button_action: modalInformation.buttonAction,
      city: listingInformation.city,
      community_id: listingInformation.id,
      community_name: listingInformation.communityName,
      home_price: listingInformation.price,
      home_type: listingInformation.homeStatus,
      market_id: listingInformation.marketId,
      market_name: listingInformation.marketName,
      plan_id: listingInformation.planId,
      primary_image_url: listingInformation.imageUrl,
      state: listingInformation.stateAbbreviation,
      zip: listingInformation.zip,
    };
  
    return customProperties;
}

/**
 * triggerPageViewEvent: Dispatch the page view on window load.
 */
const triggerPageViewEvent = function () {
    const customProperties = {
      section: "Search_Results_Page",
    };
    store.dispatch(segmentPageView({ customProperties }));
};
  
onWindowLoad$.subscribe(triggerPageViewEvent);

/**
 * Dispatches a Segment event when a search is done.
 * in the typeahead.
 */
const triggerSearchExecutedEvent = () => {
  const searchTermValue = selectSearchTerm(store.getState());
  const customProperties = {
    search_term: searchTermValue,
    search_location: 'Search Results Page',
  };

  store.dispatch(segmentTrack({ eventName: "Search_Executed", customProperties })    );
};

typeahead
  .getSuggestionListClickedObserver()
  .subscribe(triggerSearchExecutedEvent);

/**
 * Dispatches a segment event when a
 * search box button is clicked, capturing the search query and location.
 * @param inputSearchBoxValue - The `inputSearchBoxValue` parameter is the value entered by the user
 * in the search box before clicking the search button.
 */
const triggerSearchBoxButtonClickedEvent = (inputSearchBoxValue) => {
    const customProperties = {
      new_search_term: inputSearchBoxValue,
    };
    
    store.dispatch(segmentTrack({ eventName: 'Search_Button_Clicked', customProperties }));
};
    
typeahead
.getSubmitEventObserver()
.subscribe(triggerSearchBoxButtonClickedEvent);

  /**
 * The getCardImageUrls function takes a cardElement as an argument and returns an object with the following properties:
 * primaryImage, secondaryImage, terciaryImage.
 * if there are no images in the cardElement then each property will be assigned to an empty string.
 * This can be used to other events in the list view section.
 * @param cardElement Get the image elements from the card element
 *
 * @return An object with primaryImage, secondaryImage, terciaryImage.
 */
const getCardImageUrls = (cardElement) => {
    const imageElements = cardElement.querySelectorAll('img');
  
    if (imageElements) {
      return {
        primaryImage: imageElements[0]?.src || '',
        secondaryImage: imageElements[1]?.src || '',
        terciaryImage: imageElements[2]?.src || '',
      };
    }
  
    return {};
};

/**
* Retrieves custom properties Paid Plan cards
* related to a lead button click on a paid plan within a card element.
* @param cardData - The `cardData` parameter in the
* `getLeadButtonClickedCallToActionPaidPlanCustomProperties` function is expected to be an HTML
* element that represents a card containing data attributes related to a property listing. The
* function extracts specific data attributes from this element to create custom properties for
* tracking user interactions with
* @returns Returns an object  * containing custom properties related to a lead button click action
* on a paid plan. The custom properties include information to be logged
*/
const getLeadButtonClickedCallToActionPaidPlanCustomProperties = function (cardData){

    const leadButton = cardData.querySelector('[data-lead-generator-opener]');
    const leadButtonDataset = leadButton.dataset || [];
    const cardDataset = cardData.dataset;
  
    const primaryImageUrl = cardDataset.imageUrl !== ''
      ? cardDataset.imageUrl
      : getCardImageUrls(cardData).primaryImage;
    
    const customProperties = {
        pageType: 'Learning Center',
        market_id: cardDataset.marketId,
        market_name: cardDataset.marketName,
        city: cardDataset.city,
        state: cardDataset.stateAbbreviation,
        zip: cardDataset.zip,
        builder_id: cardDataset.builderId,
        builder_name: cardDataset.brandName,
        community_id: cardDataset.communityId,
        community_name: cardDataset.communityName,
        primary_image_url: primaryImageUrl,
        button_action: leadButtonDataset.buttonAction,
        plan_id: cardDataset.planId,
        home_type: cardDataset.homeStatus,
        home_price: cardDataset.price,
      };
  
      return customProperties;
}

/**
* Retrieves custom properties Paid Spec cards
* related to a lead button click on a card element.
* @param cardData - HTML element that represents a card
* containing various data attributes
* @returns Returns an object containing custom properties related to a lead button click
* action on a card element. The custom properties include information to be used for Segment events.
*/
const getLeadButtonClickedCallToActionPaidSpecCustomProperties = function (cardData){

    const leadButton = cardData.querySelector('[data-lead-generator-opener]');
    const leadButtonDataset = leadButton.dataset || [];
    const cardDataset = cardData.dataset;
  
    const primaryImageUrl = cardDataset.imageUrl !== ''
      ? cardDataset.imageUrl
      : getCardImageUrls(cardData).primaryImage;
    
    const customProperties = {
        pageType: 'Learning Center',
        market_id: cardDataset.marketId,
        market_name: cardDataset.marketName,
        city: cardDataset.city,
        state: cardDataset.stateAbbreviation,
        zip: cardDataset.zip,
        builder_id: cardDataset.builderId,
        builder_name: cardDataset.brandName,
        community_id: cardDataset.communityId,
        community_name: cardDataset.communityName,
        primary_image_url: primaryImageUrl,
        button_action: leadButtonDataset.buttonAction,
        spec_id: cardDataset.spec_id,
        home_type: cardDataset.homeStatus,
        home_price: cardDataset.price,
      };
  
      return customProperties;
}

/**
* Retrieves custom properties for Paid Community card
* related to a lead button click on a card element.
* @param cardData - HTML element that represents a card
* containing various data attributes
* @returns Returns an object containing custom properties related to a lead button click
* action on a card element. The custom properties include information to be used for Segment events.
*/
const getLeadButtonClickedPaidCommunityCustomProperties = function (cardData){

    const leadButton = cardData.querySelector('[data-lead-generator-opener]');
    const leadButtonDataset = leadButton.dataset || [];
    const cardDataset = cardData.dataset;
  
    const primaryImageUrl = cardDataset.imageUrl !== ''
      ? cardDataset.imageUrl
      : getCardImageUrls(cardData).primaryImage;
    
    const customProperties = {
        pageType: 'Learning Center',
        market_id: cardDataset.marketId,
        market_name: cardDataset.marketName,
        city: cardDataset.city,
        state: cardDataset.stateAbbreviation,
        zip: cardDataset.zip,
        builder_id: cardDataset.builderId,
        builder_name: cardDataset.brandName,
        community_id: cardDataset.communityId,
        community_name: cardDataset.communityName,
        primary_image_url: primaryImageUrl,
        button_action: leadButtonDataset.buttonAction,
        home_type: cardDataset.homeStatus,
        home_price: cardDataset.price,
      };
  
      return customProperties;
}

/**
 * Dispatches a Segment Event When the submitt button is clicked.
 * @param customProperties - is the custom properties of the event.
 */
const triggerModalSubmittedEvent = function(customProperties) {
    store.dispatch(segmentTrack({ eventName: 'Lead Modal Form Submitted', customProperties }));
}
  
/**
 * Dispatches an event to identify a lead when a modal
 * form is submitted.
 */
const triggerIdentifyModalSubmission = () => {
    store.dispatch(segmentIdentify({ eventName: 'Lead Modal Form Submitted' }));
};

/**
 * Dispatches a Segment event when listing card is clicked.
 * @param data - is the data card on the listings section.
 */
const triggerListingClick = (data) => {
    const customProperties = {
      listing_id: data.card.dataset.id,
      listing_name: data.card.dataset.name,
      interaction_type: data.interactionType,
    };
  
    store.dispatch(
      segmentTrack({ eventName: "Listing_Interacted", customProperties })
    );
};

/**
 * Dispatches a tracking event when the lead button is
 * clicked.
 * @param customProperties - The `customProperties` parameter in the `triggerLeadButtonClickedEvent`
 * function is likely intended to be an object that can hold additional properties or data related to
 * the event. These properties could include information such as the user's ID, timestamp, or any other
 * relevant details that you may want to track
 */
const triggerLeadButtonClickedEvent = function (customProperties){
    store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties }));
}

/* Observable stream `callToActionStandardCardClick$` that listens for
click events in the `listingForYouSectionClick$` stream. It filters out events that are considered
call-to-action events, then maps the event to the corresponding standard card element. It then
filters out any falsy values before emitting the final result. */
const callToActionStandardCardClick$ = listingForYouSectionClick$.pipe(
    filter(isCallToAction),
    map(getStandardCardElement),
    filter(Boolean),
);

/* Observable stream `callToActionCommunityCardClick$` that listens for
click events in the `listingForYouSectionClick$` stream. It filters the events to only include those
that are both a call to action and a community standard card. It then maps the event to get the
standard card element, filters out any falsy values, and finally maps the event to get custom
properties for a lead button clicked in a paid community. */
const callToActionCommunityCardClick$ = callToActionStandardCardClick$.pipe(
    filter((standardCard) => isCommunityStandardCard(standardCard.dataset)),
    map(getLeadButtonClickedPaidCommunityCustomProperties)
);

/* Observable stream `callToActionSpecCardClick$` that listens for click
events in the `listingForYouSectionClick$` stream. It filters the events to only include those that
are both a call to action and a standard card, then maps the event to extract the standard card
element. It then filters out any falsy values, maps the event to extract custom properties related
to a lead button click on a paid spec, and returns the final observable stream
`callToActionSpecCardClick$`. */
const callToActionSpecCardClick$ = callToActionStandardCardClick$.pipe(
    filter((standardCard) => isSpecStandardCard(standardCard.dataset)),
    map(getLeadButtonClickedCallToActionPaidSpecCustomProperties)
);

/* Observable stream `callToActionPlanCardClick$` that listens for click
events in the `listingForYouSectionClick$` stream. It filters the events to only include those that
are both a call to action and a standard plan card. It then maps the event to get the standard card
element, filters out any falsy values, and finally maps the event to get custom properties for a
paid plan call to action button click. */
const callToActionPlanCardClick$ = callToActionStandardCardClick$.pipe(
    filter((standardCard) => isPlanStandardCard(standardCard.dataset)),
    map(getLeadButtonClickedCallToActionPaidPlanCustomProperties)
);

merge(
    callToActionCommunityCardClick$,
    callToActionSpecCardClick$,
    callToActionPlanCardClick$).subscribe(triggerLeadButtonClickedEvent);
  
  /* Observable stream is set up to track when a user clicks on
    the favorite icon within a standard card element in the listing for you section and triggers the
    `triggerListingClick` function with the appropriate custom properties. */
  const favoriteIconClick$ = listingForYouSectionClick$.pipe(
    filter((event) => isFavoriteIcon(event)),
    map((event) => ({
      interactionType: "favorite",
      card: getStandardCardElement(event),
    }))
  );
  
  /* Observable stream that listens for click events within the listing for you section.
     It filters the events to only include those where the clicked element is a linkable
     listing element (determined by the `isLinkableListingElement`
    function). */
  const linkableListingElementClick$ = listingForYouSectionClick$.pipe(
    filter((event) => isLinkableListingElement(event)),
    map((event) => ({
      interactionType: "link_click",
      card: getStandardCardElement(event),
    }))
  );
  
  /* The `cardClick$` constant is creating an observable stream that listens for click events within
    the listing for you section. It filters the events to only include those where the clicked element
    is a standard card element (determined by the `isStandardCard` function). */
  const cardClick$ = listingForYouSectionClick$.pipe(
    filter((event) => isStandardCard(event)),
    map((event) => ({
      interactionType: "card_click",
      card: getStandardCardElement(event),
    }))
  );
  
  /* Observable stream `listingForYouSectionReady$` that listens for
    a custom event named 'listingForYouSectionReady' triggered on the `document.body`. Once this
    custom event is detected, the subsequent `subscribe` function is called. Within the `subscribe`
    function, it sets up subscriptions for different observable streams (`favoriteIconClick$`,
    `linkableListingElementClick$`, `cardClick$`) to trigger the
    `triggerListingClick` function when specific conditions are met. */
  merge(
    favoriteIconClick$,
    linkableListingElementClick$,
    cardClick$
  ).subscribe(triggerListingClick);

  // Observers to ModalSubmitted Event Logic
const modalSubmittedClick$ = store$.pipe(
    filter(selectFetchingModal),
    map(selectModal),
    filter(Boolean),
    filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  );
  
  const modalSubmittedSpecClick$ = modalSubmittedClick$.pipe(
    filter((modalInformation) => isSpecStandardCard(modalInformation.listingInformation)),
    map(triggerPaidSpecModalSubmitted)
  );
  
  const modalSubmittedPlanClick$ = modalSubmittedClick$.pipe(
    filter((modalInformation) => isPlanStandardCard(modalInformation.listingInformation)),
    map(triggerPaidPlanModalSubmitted)
  );
  
  const modalSubmittedCommunityClick$ = modalSubmittedClick$.pipe(
    filter((modalInformation) => isCommunityStandardCard(modalInformation.listingInformation)),
    map(triggerPaidCommunityModalSubmitted)
  );
  
  merge(
    modalSubmittedSpecClick$,modalSubmittedPlanClick$,modalSubmittedCommunityClick$
  ).subscribe((customProperties) => {
    triggerModalSubmittedEvent(customProperties)
    triggerIdentifyModalSubmission()
  });